import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, updateTrashedDocsStatus } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Doc } from '../../../../../types';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Typography from '@mui/material/Typography';

interface DriveTrashDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDocs: Doc[];
  driveId: string;
  selectedFolder: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveTrashDialog({
  open,
  handleClose,
  userEmail,
  selectedDocs,
  driveId,
  selectedFolder,
  changeLogRoles,
  isAllUsers,
}: DriveTrashDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateTrashedDocsStatus(userEmail, selectedDocs, true, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'The documents or folders were moved to trash successfully!',
      });

      // update all folders
      queryClient.invalidateQueries({ queryKey: [UserKeys.DriveFolders] });

      // update docs of current folder
      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, userEmail, selectedFolder],
      });

      // update docs of trash folder
      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, userEmail, `${driveId}.trash`],
      });
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue moving the selected documents or folders to trash. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.trash_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.trash_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanTrashDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Move to Trash"
      actingAs={userEmail}
      dialogContent={
        <>
          {selectedDocs.length === 1 ? (
            <Typography>
              Move <strong>{selectedDocs[0].title}</strong> to trash?
            </Typography>
          ) : (
            <Typography>
              Move <strong>{selectedDocs.length} selected items </strong>to
              trash?
            </Typography>
          )}

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Move to Trash',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
